import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { FormInput } from '@/components/FormInput';
import { FormSelect } from '@/components/FormSelect';
import { FormTextarea } from '@/components/FormTextarea';
import { useToast } from '@/hooks/useToast';
import { useManagerSuggestions } from '@/features/Vacations/hooks/data/useManagerSuggestions';
import { ClientEmailsInput } from '@/features/Vacations/components/VacationRequestForm/components/ClientEmailsInput';
import { formatDuration, overtimeDurationMinutes, type OvertimeCompensationType } from '../../../../../shared/dpRequests';
import { AttachmentPicker } from '../AttachmentPicker';
import { ChoiceCards } from '../ChoiceCards';
import { ManagerField } from '../ManagerField';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { useSubmitOvertime } from '../../hooks/data/useDpRequestMutations';
import { overtimeFormSchema } from '../../models/overtime.schemas';
import { DP_REQUESTS_PATH } from '../../routes';
import { COMPENSATION_OPTIONS, OTHER_PROJECT, OVERTIME_STEPS } from './overtimeForm.definitions';
import type { OvertimeErrors, OvertimeFormProps } from './overtimeForm.types';

export function OvertimeForm({ context, collaboratorId }: OvertimeFormProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const submit = useSubmitOvertime();
    const [uploading, setUploading] = useState(false);
    const { suggestions } = useManagerSuggestions(collaboratorId || null);

    const hasProjects = context.projects.length > 0;
    const [projectChoice, setProjectChoice] = useState(context.projects.length === 1 ? context.projects[0] : '');
    const [projectOther, setProjectOther] = useState('');
    const [internalProject, setInternalProject] = useState(false);
    const [clientEmails, setClientEmails] = useState<string[]>([]);
    const [raroManagerEmail, setRaroManagerEmail] = useState('');
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [reason, setReason] = useState('');
    const [compensationType, setCompensationType] = useState<OvertimeCompensationType | ''>('');
    const [raroFiles, setRaroFiles] = useState<PendingFile[]>([]);
    const [clientFiles, setClientFiles] = useState<PendingFile[]>([]);
    const [errors, setErrors] = useState<OvertimeErrors>({});

    const projectName = !hasProjects || projectChoice === OTHER_PROJECT ? projectOther : projectChoice;
    const duration = overtimeDurationMinutes(startTime, endTime);
    const busy = submit.isPending || uploading;

    async function handleSubmit() {
        const parsed = overtimeFormSchema.safeParse({
            projectName,
            internalProject,
            clientManagerEmails: internalProject ? [] : clientEmails,
            raroManagerEmail,
            date,
            startTime,
            endTime,
            reason,
            compensationType,
        });
        const next: OvertimeErrors = {};
        if (!parsed.success) {
            parsed.error.issues.forEach(issue => {
                const field = issue.path[0] as keyof OvertimeErrors;
                if (!next[field]) next[field] = issue.message;
            });
        }
        if (raroFiles.length === 0) next.attachments = 'Anexe o PDF com a aprovação do gestor Raro Labs';
        else if (!internalProject && clientFiles.length === 0) next.attachments = 'Anexe o PDF com a aprovação do gestor cliente ou marque projeto interno';
        setErrors(next);

        if (!parsed.success || next.attachments || !user?.uid) {
            toast.error('Revise o pedido', Object.values(next)[0]);
            return;
        }

        setUploading(true);
        try {
            const toUpload = internalProject ? raroFiles : [...raroFiles, ...clientFiles];
            await withUploadedAttachments(toUpload, user.uid, attachments =>
                submit.mutateAsync({
                    projectName: parsed.data.projectName,
                    internalProject: parsed.data.internalProject,
                    clientManagerEmails: parsed.data.clientManagerEmails,
                    raroManagerEmail: parsed.data.raroManagerEmail,
                    date: parsed.data.date,
                    startTime: parsed.data.startTime,
                    endTime: parsed.data.endTime,
                    reason: parsed.data.reason,
                    compensationType: parsed.data.compensationType,
                    attachments,
                })
            );
            navigate(DP_REQUESTS_PATH);
        } catch {
            return;
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="space-y-6">
            <section className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Preencha as informações abaixo</h2>

                <dl className="grid gap-4 rounded-xl bg-raro-gray-50 px-4 py-3 sm:grid-cols-2">
                    <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-raro-gray-500">Nome completo</dt>
                        <dd className="mt-0.5 text-sm text-raro-gray-900">{context.collaboratorName}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-raro-gray-500">Regime de contrato</dt>
                        <dd className="mt-0.5 text-sm text-raro-gray-900">{context.contractRegime}</dd>
                    </div>
                </dl>

                {hasProjects && (
                    <FormSelect
                        label="Projeto que atua"
                        name="projectChoice"
                        value={projectChoice}
                        onChange={event => setProjectChoice(event.target.value)}
                        options={[...context.projects.map(project => ({ value: project, label: project })), { value: OTHER_PROJECT, label: 'Outro projeto' }]}
                        placeholder="Selecione"
                        error={projectChoice === OTHER_PROJECT ? undefined : errors.projectName}
                        disabled={busy}
                        required
                    />
                )}
                {(!hasProjects || projectChoice === OTHER_PROJECT) && (
                    <FormInput
                        label={hasProjects ? 'Nome do projeto' : 'Projeto que atua'}
                        name="projectOther"
                        value={projectOther}
                        onChange={event => setProjectOther(event.target.value)}
                        maxLength={120}
                        error={errors.projectName}
                        disabled={busy}
                        required
                    />
                )}

                <label className="flex cursor-pointer items-center gap-2 text-sm text-raro-gray-700">
                    <input
                        type="checkbox"
                        checked={internalProject}
                        onChange={event => setInternalProject(event.target.checked)}
                        disabled={busy}
                        className="h-4 w-4 rounded border-raro-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Projeto interno (sem gestor cliente)
                </label>

                {!internalProject && (
                    <div className="space-y-1.5">
                        <p className="text-sm font-semibold text-raro-gray-800">
                            E-mail do(s) gestor(es) cliente<span className="ml-1 text-rose-600">*</span>
                        </p>
                        <ClientEmailsInput
                            emails={clientEmails}
                            onAdd={email => setClientEmails(current => [...current, email])}
                            onRemove={index => setClientEmails(current => current.filter((_, position) => position !== index))}
                            disabled={busy}
                            error={errors.clientManagerEmails}
                        />
                    </div>
                )}

                <ManagerField
                    label="E-mail do gestor Raro Labs"
                    value={raroManagerEmail}
                    onChange={setRaroManagerEmail}
                    suggestions={suggestions}
                    error={errors.raroManagerEmail}
                    disabled={busy}
                />
            </section>

            <section className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Informações sobre a hora extra</h2>

                <div className="grid gap-4 sm:grid-cols-3">
                    <FormInput label="Data" name="date" type="date" value={date} onChange={event => setDate(event.target.value)} error={errors.date} disabled={busy} required />
                    <FormInput label="Hora início" name="startTime" type="time" value={startTime} onChange={event => setStartTime(event.target.value)} error={errors.startTime} disabled={busy} required />
                    <FormInput label="Hora término" name="endTime" type="time" value={endTime} onChange={event => setEndTime(event.target.value)} error={errors.endTime} disabled={busy} required />
                </div>
                {duration !== null && <p className="text-sm text-raro-gray-600">Duração: <strong>{formatDuration(duration)}</strong></p>}

                <FormTextarea
                    label="Motivo"
                    name="reason"
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    maxLength={1000}
                    showCharCount
                    rows={3}
                    error={errors.reason}
                    disabled={busy}
                    required
                />

                <ChoiceCards
                    legend="Tipo"
                    name="compensationType"
                    value={compensationType}
                    options={COMPENSATION_OPTIONS}
                    onChange={setCompensationType}
                    hint="Alinhe com o seu gestor se é COMPENSAÇÃO ou PAGAMENTO."
                    error={errors.compensationType}
                    disabled={busy}
                />
            </section>

            <section className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
                <h2 className="flex items-center gap-2 font-montserrat text-base font-semibold text-amber-900">
                    <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
                    Importante! Antes de registrar a solicitação
                </h2>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-amber-900">
                    {OVERTIME_STEPS.map(step => (
                        <li key={step}>{step}</li>
                    ))}
                </ol>
                <AttachmentPicker
                    files={raroFiles}
                    onAdd={file => setRaroFiles(current => [...current, file])}
                    onRemove={index => setRaroFiles(current => current.filter((_, position) => position !== index))}
                    label="PDF com a aprovação do gestor Raro Labs"
                    hint="Somente PDF, até 10 MB. O arquivo só é enviado quando você enviar o pedido."
                    kind="raro_manager"
                    pdfOnly
                    required
                    maxFiles={1}
                    disabled={busy}
                />
                {!internalProject && (
                    <AttachmentPicker
                        files={clientFiles}
                        onAdd={file => setClientFiles(current => [...current, file])}
                        onRemove={index => setClientFiles(current => current.filter((_, position) => position !== index))}
                        label="PDF com a aprovação do gestor cliente"
                        hint="Somente PDF, até 10 MB."
                        kind="client_manager"
                        pdfOnly
                        required
                        maxFiles={1}
                        disabled={busy}
                    />
                )}
                {errors.attachments && <p className="text-xs text-rose-700" role="alert">{errors.attachments}</p>}
            </section>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={busy}>Cancelar</Button>
                <Button onClick={() => void handleSubmit()} loading={busy} disabled={!user?.uid}>{uploading ? 'Enviando anexos…' : 'Enviar ao DP'}</Button>
            </div>
        </div>
    );
}
